import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatDate } from '@/lib/utils/date.utils';
import { OFFER_STATUS_BADGE_VARIANT, OFFER_STATUS_LABEL, PARTNER_OFFERS_STRINGS as S } from '../partner-offers.constants';
import type { PartnerOfferData } from '../api/partner-offers.api';

interface AllOffersTableProps {
  offers: PartnerOfferData[];
}

/** Plain table, not `common/DataTable` - Gate 1 determined this console is not a baseline-six admin listing. */
export function AllOffersTable({ offers }: AllOffersTableProps) {
  if (offers.length === 0) {
    return <EmptyState title={S.SECTION_EMPTY} />;
  }

  return (
    <table className="w-full">
      {/*
        Each row is a flex card rather than a table row (the gap between cards
        is the whole visual), which takes the body out of table layout - so the
        column algorithm only ever saw the header row and sized it to its own
        text. Status and Expiry sat left of the values beneath them. The header
        mirrors the row box exactly instead: same flex, same gap, same
        horizontal padding as the card, same column widths.
      */}
      <thead className="block">
        <tr className="flex items-center gap-3 px-3.5 text-left text-[11px] font-semibold tracking-wide text-slate uppercase">
          <th className="flex-1 pb-2 font-semibold">Offer Title</th>
          <th className="w-[140px] pb-2 font-semibold">Status</th>
          <th className="w-[140px] pb-2 font-semibold">Expiry</th>
        </tr>
      </thead>
      <tbody className="flex flex-col gap-3 [&>tr]:flex [&>tr]:items-center [&>tr]:gap-3">
        {/* Column widths below must stay in step with the header above. */}
        {offers.map((offer) => (
          <tr key={offer.id} className="rounded-card bg-white p-3.5 shadow-card">
            <td className="flex-1 text-p6 font-semibold text-navy">{offer.title}</td>
            <td className="w-[140px]">
              <Badge variant={OFFER_STATUS_BADGE_VARIANT[offer.status]} size="sm">
                {OFFER_STATUS_LABEL[offer.status]}
              </Badge>
            </td>
            <td className="w-[140px] text-p6 text-slate">{formatDate(offer.expiresAt, S.NO_EXPIRY)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
